import { DestroyRef, Directive, ElementRef, inject, Injectable } from '@angular/core';

/**
 * Provided on a common ancestor so a sticky header row and the scrollers beneath it
 * share one horizontal scroll position: only the header needs a scrollbar, the rest follow it.
 */
@Injectable()
export class ScrollSyncGroup {
  private readonly members = new Set<HTMLElement>();

  public register(element: HTMLElement): () => void {
    this.members.add(element);
    return () => this.members.delete(element);
  }

  public follow(source: HTMLElement): void {
    for (const member of this.members) {
      if (member !== source && Math.abs(member.scrollLeft - source.scrollLeft) > 0.5) {
        member.scrollLeft = source.scrollLeft;
      }
    }
  }
}

@Directive({
  selector: '[rmaScrollSync]',
  host: {
    '(scroll)': 'onScroll()',
  },
})
export class ScrollSyncDirective {
  private readonly group = inject(ScrollSyncGroup, { optional: true });
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  constructor() {
    const unregister = this.group?.register(this.host.nativeElement);
    inject(DestroyRef).onDestroy(() => unregister?.());
  }

  protected onScroll(): void {
    this.group?.follow(this.host.nativeElement);
  }
}
